import { Family, PlanKey } from '../types/family';
import { OccurrenceState, OccurrenceStatus } from '../types/activity';
import { DutyCheck, ExceptionRequest, ExcuseKey } from '../types/shared';
import { DEMO_DATE, WEEK_END, WEEK_START } from '../data/demo';
import { activeExcuses, hasLessonOn, isGivenAway } from './today';
import { overlapDays } from './time';
import { dutyChecksFor, dutyScore, dutySlots, isDuty, SlotState } from './duties';

const stateMark: Record<SlotState, string> = {
  urejeno: '✓',
  popravljeno: '✓',
  'ni-urejeno': '✗',
  odprto: '–',
  izpadlo: '·',
};

export interface PlanLine {
  id: string;
  label: string;
  required: number;
  units: number;
  earned: number;
  possible: number;
  hint?: string;
  pendingReview: boolean;
  excusedDays: number;
  weighted: boolean;
  kind: PlanKey | 'hisa';
  /** Število športnih terminov iz koledarja, ki štejejo v ta sklop. */
  fromCalendar?: number;
}

export interface WeekSummary {
  lines: PlanLine[];
  earned: number;
  possible: number;
  pct: number;
  threshold: number;
  missingPct: number;
  reached: boolean;
  excusedWeek: boolean;
}

export function isDoneStatus(status?: OccurrenceStatus): boolean {
  return status === 'opravljeno' || status === 'potrjeno';
}

export function equalShares(value: number, memberIds: string[]): Record<string, number> {
  if (memberIds.length === 0) return {};
  const base = Math.floor(value / memberIds.length);
  let remainder = value - base * memberIds.length;
  const shares: Record<string, number> = {};
  memberIds.forEach((id) => {
    shares[id] = (shares[id] ?? 0) + base + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
  });
  return shares;
}

const moduleFor: Partial<Record<PlanKey, keyof Family['modules']>> = {
  instrument: 'glasba',
  zivali: 'zivali',
  vecer: 'vecerniRezim',
  ucenje: 'sola',
  soba: 'hisnaOpravila',
};

function excusedDaysInWeek(exceptions: ExceptionRequest[], familyId: string, memberId: string, key: ExcuseKey): number {
  return exceptions
    .filter((e) => e.familyId === familyId && e.memberId === memberId && e.status === 'odobreno' && e.excuses.includes(key))
    .reduce((sum, e) => sum + overlapDays(e.from, e.to, WEEK_START, WEEK_END), 0);
}

// Osnovni tedenski napredek = pridobljene osnovne uteži / osnovne uteži veljavnega osebnega načrta.
// Oproščene izvedbe niso v imenovalcu; bonus točke ga ne zvišujejo.
export function computeWeek(
  family: Family,
  memberId: string,
  occ: Record<string, OccurrenceState>,
  schoolPE: boolean,
  exceptions: ExceptionRequest[] = [],
  calendarSportDates: string[] = [],
  dutyChecks: DutyCheck[] = []
): WeekSummary {
  const calendarDays = Array.from(new Set(calendarSportDates));
  const calendarToday = calendarDays.includes(DEMO_DATE);
  const member = family.members.find((m) => m.id === memberId);
  const plan = family.plans.find((p) => p.memberId === memberId);
  const rules = family.rules;
  const todayEx = activeExcuses(exceptions, family.id, memberId, DEMO_DATE);
  const lesson = hasLessonOn(family, memberId);
  const lines: PlanLine[] = [];

  plan?.items.forEach((item) => {
    const mod = moduleFor[item.key];
    if (mod && !family.modules[mod]) return;
    if (item.key === 'branje' || item.key === 'sport') {
      if (!family.modules.osebnaRutina) return;
    }
    // Soba je tedenska zadolžitev – računa se spodaj po pregledih.
    if (item.key === 'soba') return;
    let required =
      item.key === 'sport'
        ? member?.role === 'otrok'
          ? schoolPE
            ? rules.sportPerWeekChildWithSchoolPE
            : rules.sportPerWeekChild
          : rules.sportPerWeekAdult
        : item.required;
    const exDays = excusedDaysInWeek(exceptions, family.id, memberId, item.key);
    if (exDays > 0) {
      if (item.key === 'sport') required = Math.max(0, required - Math.round((exDays * required) / 7));
      else if (item.key === 'soba') required = 0;
      else if (item.weighted) required = Math.round((required * Math.max(0, 7 - exDays)) / 7);
      else required = Math.max(0, required - exDays);
    }
    const templates = family.templates.filter(
      (t) => t.planKey === item.key && family.modules[t.module] && t.assigneeIds.includes(memberId)
    );
    const doneToday = templates.filter((t) => {
      const o = occ[t.id];
      if (!o || !isDoneStatus(o.status) || isGivenAway(o, memberId)) return false;
      if (t.confirmation === 'skrbnik' && o.status !== 'potrjeno') return false;
      if (item.key === 'instrument' && lesson) return false;
      return !todayEx.includes(item.key);
    });
    const pendingReview = templates.some((t) => occ[t.id]?.status === 'v_pregledu');
    if (item.weighted) {
      const earnedToday = doneToday.reduce((s, t) => s + (t.baseValue ?? 0), 0);
      const earned = Math.min(required, item.doneBefore + earnedToday);
      lines.push({
        id: item.key,
        label: item.label,
        required,
        units: earned,
        earned,
        possible: required,
        hint: item.hint,
        pendingReview,
        excusedDays: exDays,
        weighted: true,
        kind: item.key,
      });
    } else {
      const isSport = item.key === 'sport';
      const fromCalendar = isSport ? calendarDays.length : 0;
      const todayCount = isSport && calendarToday ? 0 : doneToday.length;
      const units = Math.min(required, item.doneBefore + fromCalendar + todayCount);
      lines.push({
        id: item.key,
        label: item.label,
        required,
        units,
        earned: units * item.unitWeight,
        possible: required * item.unitWeight,
        hint: item.key === 'instrument' && lesson ? 'danes pouk – domača vadba je prostovoljna (goodies)' : item.hint,
        pendingReview,
        excusedDays: exDays,
        weighted: false,
        kind: item.key,
        fromCalendar: fromCalendar || undefined,
      });
    }
  });

  if (family.modules.hisnaOpravila) {
    const houseEx = excusedDaysInWeek(exceptions, family.id, memberId, 'hisa');
    const roomEx = excusedDaysInWeek(exceptions, family.id, memberId, 'soba');
    family.templates
      .filter((t) => isDuty(t) && t.assigneeIds.includes(memberId))
      .forEach((t) => {
        const room = t.planKey === 'soba';
        const ex = room ? roomEx : houseEx;
        const share = equalShares(t.baseValue ?? 0, t.assigneeIds)[memberId] ?? 0;
        const score = dutyScore(share, dutyChecksFor(dutyChecks, family.id, t.id));
        lines.push({
          id: t.id,
          label: room ? 'Soba' : t.title,
          required: score.counted,
          units: score.tidy,
          earned: ex > 0 ? 0 : score.earned,
          possible: ex > 0 ? 0 : score.possible,
          hint: `pregledi: ${dutySlots.map((s, i) => `${s.short} ${stateMark[score.states[i]]}`).join(' · ')}`,
          pendingReview: false,
          excusedDays: ex,
          weighted: true,
          kind: room ? 'soba' : 'hisa',
        });
      });
    family.templates
      .filter(
        (t) =>
          t.group === 'hisa' &&
          !t.planKey &&
          !isDuty(t) &&
          t.valueType === 'osnovno' &&
          t.assigneeIds.includes(memberId) &&
          (!t.nextDue || t.nextDue <= WEEK_END)
      )
      .forEach((t) => {
        const value = t.baseValue ?? 0;
        const defaultShare = equalShares(value, t.assigneeIds)[memberId] ?? 0;
        const o = occ[t.id];
        const approved = o?.status === 'potrjeno' && !isGivenAway(o, memberId);
        const possible = houseEx > 0 ? 0 : defaultShare;
        lines.push({
          id: t.id,
          label: t.title,
          required: 1,
          units: approved ? 1 : 0,
          earned: approved ? Math.min(o?.shares?.[memberId] ?? defaultShare, possible) : 0,
          possible,
          hint: t.assigneeIds.length > 1 ? `tvoj delež ${defaultShare} od ${value}` : undefined,
          pendingReview: o?.status === 'v_pregledu',
          excusedDays: houseEx,
          weighted: true,
          kind: 'hisa',
        });
      });
  }

  const earned = lines.reduce((sum, l) => sum + l.earned, 0);
  const possible = lines.reduce((sum, l) => sum + l.possible, 0);
  const excusedWeek = lines.length > 0 && possible === 0;
  const pct = possible > 0 ? Math.min(100, Math.round((earned / possible) * 100)) : excusedWeek ? 100 : 0;
  const threshold = rules.basicThresholdPct;
  return {
    lines,
    earned,
    possible,
    pct,
    threshold,
    missingPct: Math.max(0, threshold - pct),
    reached: pct >= threshold,
    excusedWeek,
  };
}
